import { orderBy } from "lodash-es";
import isNil from "lodash-es/isNil";
import { makeAutoObservable } from "mobx";

import { ACCEPT_FALLBACK_HEADER, apiClient } from "../../common/apiClient";
import { platformRoutes, resolvePlatformRequestUrl } from "../../common/platformRoutes";
import type { BaseStoreType } from "../../common/types/BaseStoreType";
import type { Notification, NotificationsResponse } from "../../common/types/notificationTypes";
import {
   type AsyncData,
   initializeWithDefaultData,
   setAsDataAvailable,
   setAsErrorOccured,
   setAsWaitingForData
} from "../../common/utils/asyncDataUtils";
import authStore from "../auth/authStore";
import { filterReadNotifications } from "./notificationsUtils";

type NotificationsStoreType = BaseStoreType & {
   unreadNotifications: AsyncData<Notification[]>;
   readNotifications: AsyncData<Notification[]>;
   cacheMetadata: {
      unreadNotifications?: { cachedAt: string };
      readNotifications?: { cachedAt: string };
   };
   fetchUnreadNotifications(): Promise<void>;
   fetchReadNotifications(): Promise<void>;
};

const notificationsStore: NotificationsStoreType = makeAutoObservable({
   unreadNotifications: initializeWithDefaultData([]),
   readNotifications: initializeWithDefaultData([]),
   cacheMetadata: {
      unreadNotifications: undefined,
      readNotifications: undefined
   },

   fetchUnreadNotifications: async () => {
      const transitionedSuccessfully = setAsWaitingForData(notificationsStore.unreadNotifications);

      if (!transitionedSuccessfully || isNil(authStore.currentCompany)) {
         return Promise.reject();
      }
      const { currentCompany: customerNumber } = authStore;
      const url = resolvePlatformRequestUrl(platformRoutes.notifications, {
         customerNumber
      });

      return apiClient(url, authStore.getSessionToken())
         .headers(ACCEPT_FALLBACK_HEADER)
         .get()
         .res((response) => {
            const cacheTimestamp = response.headers.get("X-Cache-Timestamp");
            if (cacheTimestamp) {
               notificationsStore.cacheMetadata.unreadNotifications = {
                  cachedAt: cacheTimestamp
               };
            }

            return response.json();
         })
         .then((res) => {
            const sortedResult = orderBy((res as NotificationsResponse).payload, ["added"], ["desc"]);
            setAsDataAvailable(notificationsStore.unreadNotifications, sortedResult);
         })
         .catch((errorObject) => {
            setAsErrorOccured(notificationsStore.unreadNotifications, `${errorObject}`);
         });
   },

   fetchReadNotifications: async () => {
      const transitionedSuccessfully = setAsWaitingForData(notificationsStore.readNotifications);

      if (!transitionedSuccessfully || isNil(authStore.currentCompany)) {
         return Promise.reject();
      }

      const { currentCompany: customerNumber } = authStore;
      const url = resolvePlatformRequestUrl(platformRoutes.notificationArchive, {
         customerNumber
      });

      return apiClient(url, authStore.getSessionToken())
         .headers(ACCEPT_FALLBACK_HEADER)
         .get()
         .res((response) => {
            const cacheTimestamp = response.headers.get("X-Cache-Timestamp");
            if (cacheTimestamp) {
               notificationsStore.cacheMetadata.readNotifications = {
                  cachedAt: cacheTimestamp
               };
            }

            return response.json();
         })
         .then((res) => {
            const result = filterReadNotifications(
               (res as NotificationsResponse).payload,
               notificationsStore.unreadNotifications.data
            );
            const sortedResult = orderBy(result, ["added"], ["desc"]);
            setAsDataAvailable(notificationsStore.readNotifications, sortedResult);
         })
         .catch((errorObject) => {
            setAsErrorOccured(notificationsStore.readNotifications, `${errorObject}`);
         });
   },

   clearCompanySpecificData: () => {
      notificationsStore.unreadNotifications = initializeWithDefaultData([]);
      notificationsStore.readNotifications = initializeWithDefaultData([]);
      notificationsStore.cacheMetadata = {
         unreadNotifications: undefined,
         readNotifications: undefined
      };
   }
} satisfies NotificationsStoreType);

export default notificationsStore;
